/**
 * Active-location context for the topbar branch switcher (ML-01). Reads the
 * tenant's `locations` (RLS-scoped); holds the selected branch (or "all"). Pages
 * that are location-aware (classes, branches) read `activeLocationId`.
 */
import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import { useAuth } from './auth.js';
import { supabase } from './supabase.js';

export interface LocationRow {
  id: string;
  name: string;
}

interface LocationValue {
  locations: LocationRow[];
  loading: boolean;
  activeLocationId: string | null; // null = all branches
  setActiveLocationId: (id: string | null) => void;
}

const LocationContext = createContext<LocationValue | null>(null);

export function LocationProvider({ children }: { children: ReactNode }): ReactNode {
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['locations', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<LocationRow[]> => {
      const { data, error } = await supabase
        .from('locations')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as LocationRow[];
    },
  });

  const value = useMemo<LocationValue>(
    () => ({
      locations: data ?? [],
      loading: isLoading,
      activeLocationId,
      setActiveLocationId,
    }),
    [data, isLoading, activeLocationId],
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocations(): LocationValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocations must be used within <LocationProvider>');
  return ctx;
}
